import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress, Backdrop, Typography, Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MultiSelect } from "primereact/multiselect";
import { MdRefresh } from "react-icons/md";

import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";
import {
  readStoredSelection,
  writeStoredSelection,
  emptySelection,
  toFilterBody,
  optionsFromFilterResponse,
  pruneSelection,
  kpiCards,
  changeLabel,
  categoryDonutSeries,
  monthlySeries,
  disposalMethodBarSeries,
  topWasteTypes,
  methodMixSeries,
  dataQualitySeries,
  gwpChips,
  editionChips,
  hasAnyData,
  COLORS,
} from "./utils/wasteDashboardUtils";

const FILTERS = [
  { key: "year", label: "Year", from: "year", kind: "year" },
  { key: "site", label: "Site", from: "site" },
  { key: "legal", label: "Legal Entity", from: "legalEntity" },
  { key: "business", label: "Business Unit", from: "businessUnit" },
  { key: "country", label: "Country", from: "country" },
  { key: "category", label: "Waste category", from: "wasteCategory" },
  { key: "method", label: "Disposal method", from: "disposalMethod" },
];

const NoDataComponent = ({ title, onConfigureClick }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "280px",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px dashed #dee2e6",
      textAlign: "center",
      padding: "20px",
    }}
  >
    <h6 style={{ color: "#494949", marginBottom: "8px" }}>{title}</h6>
    <p style={{ color: "#707070", fontSize: "14px", marginBottom: "16px" }}>
      No waste data yet. Configure the "Waste Generated in Operations" upload for a site to see this dashboard.
    </p>
    <Button variant="outlined" onClick={onConfigureClick} sx={{ textTransform: "none", borderColor: "#407775", color: "#407775" }}>
      Configure upload
    </Button>
  </div>
);

const baseChart = (title) => ({
  chart: { backgroundColor: "transparent", style: { fontFamily: "inherit" } },
  title: { text: title, align: "left", style: { fontSize: "16px", color: "#494949", fontWeight: "400" } },
  credits: { enabled: false },
  exporting: { enabled: false },
  legend: { itemStyle: { color: "#707070", fontWeight: "400" } },
});

function WasteDashboard() {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState(() => readStoredSelection());
  const [options, setOptions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const handleConfigureClick = () => navigate("/allconfiguration");

  const post = useCallback(
    async (route, body) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}${route}`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8", username: username },
        body: JSON.stringify(body),
      });
      if (response.status !== 200) throw new Error(`${route} returned ${response.status}`);
      const json = await response.json();
      if (json && json.success === false) throw new Error(json.data || "Request failed");
      return json;
    },
    [username]
  );

  const load = useCallback(
    async (sel) => {
      setLoading(true);
      setError("");
      try {
        const body = toFilterBody(sel, companyid);
        const [filterResponse, data] = await Promise.all([post("waste-dashboard-filters", body), post("waste-dashboard", body)]);
        setOptions(filterResponse);
        setSummary(data);
        setLastRefreshTime(new Date());
        const pruned = pruneSelection(sel, filterResponse);
        if (JSON.stringify(pruned) !== JSON.stringify(sel)) {
          setSelection(pruned);
          writeStoredSelection(pruned);
        }
      } catch (e) {
        console.error(e);
        setError(e.message || "Could not load the waste dashboard.");
      } finally {
        setLoading(false);
      }
    },
    [companyid, post]
  );

  useEffect(() => {
    load(selection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilterChange = (key, values) => {
    const next = { ...selection, [key]: values || [] };
    setSelection(next);
    writeStoredSelection(next);
    load(next);
  };

  const handleResetFilters = () => {
    const next = emptySelection();
    setSelection(next);
    writeStoredSelection(next);
    load(next);
  };

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  const formatDateTime = (date) =>
    date ? date.toLocaleString(undefined, { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  // ── Derived chart data ─────────────────────────────────────────────────────
  const cards = useMemo(() => kpiCards(summary), [summary]);
  const donut = useMemo(() => categoryDonutSeries(summary && summary.byCategory), [summary]);
  const monthly = useMemo(() => monthlySeries(summary && summary.monthly), [summary]);
  const methods = useMemo(() => disposalMethodBarSeries(summary && summary.byDisposalMethod), [summary]);
  const types = useMemo(() => topWasteTypes(summary && summary.byWasteType, 8), [summary]);
  const mix = useMemo(() => methodMixSeries(summary && summary.methodMix), [summary]);
  const quality = useMemo(() => dataQualitySeries(summary && summary.dataQuality), [summary]);
  const gwp = useMemo(() => gwpChips(summary && summary.gwpBases), [summary]);
  const editions = useMemo(() => editionChips(summary && summary.editions), [summary]);
  const showData = hasAnyData(summary);

  const monthlyChart = {
    ...baseChart("Waste by month: recovered vs disposed (tonnes) and emissions"),
    chart: { ...baseChart().chart, type: "column", height: 340 },
    xAxis: { categories: monthly.categories, labels: { style: { color: "#707070" } } },
    yAxis: [
      { title: { text: "Tonnes", style: { color: "#707070" } }, stackLabels: { enabled: false } },
      { title: { text: "tCO2e", style: { color: "#707070" } }, opposite: true },
    ],
    plotOptions: { column: { stacking: "normal", borderWidth: 0 } },
    tooltip: { shared: true, valueDecimals: 2 },
    series: [
      { name: "Diverted from disposal", data: monthly.diverted, color: COLORS.diverted },
      { name: "Directed to disposal", data: monthly.disposed, color: COLORS.disposed },
      { name: "Emissions (tCO2e)", type: "line", yAxis: 1, data: monthly.emissions, color: COLORS.emissions, marker: { enabled: true } },
    ],
  };

  const donutChart = {
    ...baseChart("Tonnes by waste category"),
    chart: { ...baseChart().chart, type: "pie", height: 340 },
    tooltip: { pointFormat: "<b>{point.y} t</b> · {point.emissions} tCO2e" },
    plotOptions: { pie: { innerSize: "60%", dataLabels: { enabled: true, format: "{point.name}: {point.percentage:.1f}%", style: { color: "#494949", fontWeight: "400" } } } },
    series: [{ name: "Tonnes", data: donut }],
  };

  const methodChart = {
    ...baseChart("Tonnes by disposal method"),
    chart: { ...baseChart().chart, type: "bar", height: Math.max(300, 60 + methods.categories.length * 44) },
    xAxis: { categories: methods.categories, labels: { style: { color: "#707070" } } },
    yAxis: { title: { text: "Tonnes", style: { color: "#707070" } } },
    legend: { enabled: false },
    tooltip: { pointFormat: "<b>{point.y} t</b> · {point.emissions} tCO2e · {point.disposition}" },
    plotOptions: { bar: { borderWidth: 0, dataLabels: { enabled: true, format: "{point.y}" } } },
    series: [{ name: "Tonnes", data: methods.data }],
  };

  const typesChart = {
    ...baseChart("Top waste types"),
    chart: { ...baseChart().chart, type: "column", height: 340 },
    xAxis: { categories: types.categories, labels: { style: { color: "#707070" }, rotation: -30 } },
    yAxis: [
      { title: { text: "Tonnes", style: { color: "#707070" } } },
      { title: { text: "tCO2e", style: { color: "#707070" } }, opposite: true },
    ],
    tooltip: { shared: true, valueDecimals: 2 },
    series: [
      { name: "Tonnes", data: types.tonnes, color: COLORS.nonHazardous, borderWidth: 0 },
      { name: "Emissions (tCO2e)", type: "line", yAxis: 1, data: types.emissions, color: COLORS.emissions },
    ],
  };

  const qualityChart = {
    ...baseChart("Calculation method and data quality (rows)"),
    chart: { ...baseChart().chart, type: "bar", height: 300 },
    xAxis: { categories: ["Method"], visible: false },
    yAxis: { title: { text: "Rows", style: { color: "#707070" } } },
    plotOptions: { bar: { stacking: "normal", borderWidth: 0 } },
    tooltip: { pointFormat: "<b>{series.name}</b>: {point.y} rows" },
    series: [
      ...mix.map((m) => ({ name: m.name, data: [m.y], color: m.name.startsWith("Method 1") ? COLORS.diverted : m.name.startsWith("Method 2") ? COLORS.nonHazardous : COLORS.muted })),
    ],
  };

  return (
    <div className={`wrapper ${isSidebarOpen ? "" : "toggled"}`}>
      <div className="wrapper">
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container" style={{ marginLeft: "auto", marginRight: "auto", paddingLeft: "30px", paddingRight: "20px" }}>
          <div className="row">
            <div className="col-12 col-md-8">
              <div className="steps__containt-hader">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h3 className="title mb-0">Waste</h3>
                  {lastRefreshTime && (
                    <div style={{ fontSize: "12px", color: "#707070", fontWeight: "400" }}>Last Refresh: {formatDateTime(lastRefreshTime)}</div>
                  )}
                </div>
                <div role="presentation" className="mt-2">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/esg-dashboard" className="link">
                      Dashboard
                    </Link>
                    <Typography color="text.primary">Waste</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-end flex-wrap gap-2">
              <Button className="refresh-cta" variant="text" startIcon={<MdRefresh />} onClick={() => load(selection)}>
                Refresh
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="row g-3">
            {FILTERS.map((f) => (
              <div key={f.key} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <label className="form-label">{f.label}</label>
                <MultiSelect
                  value={selection[f.key]}
                  onChange={(e) => onFilterChange(f.key, e.target.value)}
                  options={optionsFromFilterResponse(options && options[f.from], f.kind)}
                  optionLabel="label"
                  optionValue="value"
                  optionDisabled="disabled"
                  placeholder="All"
                  maxSelectedLabels={1}
                  className="w-100"
                  showClear
                  showSelectAll
                  selectAllLabel="Select All"
                  filter={f.key === "site" || f.key === "legal" || f.key === "business"}
                />
              </div>
            ))}
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 d-flex align-items-end">
              <Button
                onClick={handleResetFilters}
                variant="outlined"
                startIcon={<MdRefresh />}
                sx={{ backgroundColor: "#f5f5f5", color: "#666", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", textTransform: "none", minHeight: "40px", width: "100%" }}
              >
                Reset
              </Button>
            </div>
          </div>

          {error && (
            <div className="alert alert-warning mt-4" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && !showData && (
            <div className="mt-4">
              <NoDataComponent title="Waste emissions" onConfigureClick={handleConfigureClick} />
            </div>
          )}

          {showData && (
            <>
              {/* KPI cards */}
              <div className="row mt-4">
                {cards.map((card) => {
                  const change = card.change != null ? changeLabel(card.change) : null;
                  return (
                    <div key={card.key} className="col-12 col-md-6 col-xl-4 mb-4">
                      <div className="card border-0 shadow emissions-card h-100" style={{ borderRadius: "0px", padding: "22px 24px" }}>
                        <h6 className="mb-2 emissions-title text-start" style={{ color: "#707070", fontWeight: 600, fontSize: "15px" }}>
                          {card.title}
                        </h6>
                        <div className="d-flex align-items-end justify-content-between">
                          <h4 className="fw-bold mb-0 data" style={{ fontSize: "26px", color: card.attention ? "#B26A00" : "#494949" }}>
                            {card.value}
                          </h4>
                          {change && (
                            <small style={{ fontSize: "12px", color: change.direction === "up" ? "#B3261E" : change.direction === "down" ? "#2E7D4F" : "#707070" }}>
                              {change.text}
                            </small>
                          )}
                        </div>
                        {card.sub && (
                          <small className="text-muted" style={{ fontSize: "12px", marginTop: "6px", display: "block" }}>
                            {card.sub}
                          </small>
                        )}
                        {card.key === "review" && card.attention && (
                          <Button size="small" sx={{ textTransform: "none", padding: 0, marginTop: "6px", justifyContent: "flex-start", color: "#407775" }} onClick={() => navigate("/allconfiguration?review=waste")}>
                            Review now
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Provenance chips */}
              <div className="d-flex flex-wrap gap-2 mb-3" style={{ fontSize: "12px", color: "#707070" }}>
                <span>Factor basis:</span>
                {gwp.map((c) => (
                  <span key={c} className="badge" style={{ background: "#EBEFF3", color: "#3A4855", fontWeight: 500 }}>
                    {c}
                  </span>
                ))}
                {gwp.length > 1 && <span style={{ color: "#B26A00" }}>· mixed GWP bases in this total</span>}
                <span className="ms-3">Editions:</span>
                {editions.map((c) => (
                  <span key={c} className="badge" style={{ background: "#EBEFF3", color: "#3A4855", fontWeight: 500 }}>
                    {c}
                  </span>
                ))}
              </div>

              {/* Charts */}
              <div className="row g-4">
                <div className="col-12 col-lg-8">
                  <div className="charts card border-0 shadow p-3">
                    {monthly.categories.length ? <HighchartsReact highcharts={Highcharts} options={monthlyChart} /> : <NoDataComponent title="Waste by month" onConfigureClick={handleConfigureClick} />}
                  </div>
                </div>
                <div className="col-12 col-lg-4">
                  <div className="charts card border-0 shadow p-3">
                    {donut.length ? <HighchartsReact highcharts={Highcharts} options={donutChart} /> : <NoDataComponent title="Tonnes by waste category" onConfigureClick={handleConfigureClick} />}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts card border-0 shadow p-3">
                    {methods.categories.length ? <HighchartsReact highcharts={Highcharts} options={methodChart} /> : <NoDataComponent title="Tonnes by disposal method" onConfigureClick={handleConfigureClick} />}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts card border-0 shadow p-3">
                    {types.categories.length ? <HighchartsReact highcharts={Highcharts} options={typesChart} /> : <NoDataComponent title="Top waste types" onConfigureClick={handleConfigureClick} />}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts card border-0 shadow p-3">
                    {mix.length ? <HighchartsReact highcharts={Highcharts} options={qualityChart} /> : <NoDataComponent title="Calculation method" onConfigureClick={handleConfigureClick} />}
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="charts card border-0 shadow p-3 h-100">
                    <div style={{ fontSize: "16px", color: "#494949", marginBottom: "12px" }}>Data quality tiers</div>
                    <table className="table table-sm" style={{ fontSize: "14px" }}>
                      <tbody>
                        {quality.map((q) => (
                          <tr key={q.name}>
                            <td style={{ color: "#494949" }}>{q.name}</td>
                            <td className="text-end" style={{ fontVariantNumeric: "tabular-nums" }}>{q.y} rows</td>
                          </tr>
                        ))}
                        {quality.length === 0 && (
                          <tr>
                            <td style={{ color: "#707070" }}>No rows in the selection.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div style={{ fontSize: "12px", color: "#707070" }}>
                      High = supplier-specific factor (Method 1). Medium = DEFRA waste-type factor (Method 2); "(proxy)" marks the closest available category. Rows without a factor are held for review and excluded from totals.
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WasteDashboard;
